import { Close } from "@mui/icons-material";
import { Divider, IconButton, Table, TableBody, TableCell, TableRow } from "@mui/material";
import ProgressBar from "components/ProgressBar";
import { TestCaseResult } from "generated/graphql";
import { pluralize } from "helpers/coll";

function useRunStats(testResults: TestCaseResult[]) {
  const passingCount = testResults.filter((r) => r.responseResults.every((rr) => rr.passed)).length;
  const cellValueScores = testResults.flatMap((r) =>
    r.responseResults.flatMap((rr) =>
      rr.cellResults.map((cellResult) => ({
        cellId: cellResult.cellId,
        cellName: cellResult.cellName,
        cellValueScore: cellResult.cellValueScore,
      }))
    )
  );

  const cellScoreMap = new Map();

  cellValueScores.forEach(({ cellId, cellName, cellValueScore }) => {
    if (!cellScoreMap.has(cellId)) {
      cellScoreMap.set(cellId, { cellId, cellName, sum: 0, count: 0 });
    }
    const entry = cellScoreMap.get(cellId);
    entry.sum += cellValueScore;
    entry.count += 1;
  });

  const cellAverage = Array.from(cellScoreMap.values()).map(({ cellId, cellName, sum, count }) => ({
    cellId,
    cellName,
    cellValueScore: sum / count,
  }));

  return {
    passingCount,
    cellAverage,
  };
}

type TestRunSummaryProps = {
  count: number;
  results: TestCaseResult[];
  onDismiss: () => void;
  running: boolean;
};

export function TestRunSummary({ count, results, onDismiss, running }: TestRunSummaryProps) {
  const { passingCount, cellAverage } = useRunStats(results);
  const testText = pluralize(count, "test");
  const title = running ? `Running ${count} ${testText}...` : `Ran ${count} ${testText}.`;
  return (
    <div className="bg-white rounded-md shadow flex flex-col p-4 my-2">
      <div className="flex items-center gap-2 justify-between">
        <p className="text-md font-semibold">
          {title} ({passingCount} passed)
        </p>
        <IconButton onClick={onDismiss}>
          <Close />
        </IconButton>
      </div>
      <Divider />
      <Table>
        <TableRow>
          <TableCell></TableCell>
          <TableCell>
            <p className="text-xs font-semibold">Average Score</p>
          </TableCell>
        </TableRow>
        <TableBody>
          {cellAverage &&
            cellAverage.map((cell) => (
              <TableRow key={cell.cellId}>
                <TableCell>
                  <p className="text-xs font-semibold">{cell.cellName}</p>
                </TableCell>
                <TableCell>
                  <ProgressBar size="medium" showBar={false} value={cell.cellValueScore * 100} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <Divider />
    </div>
  );
}

export default TestRunSummary;
